import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'Components/Base/Modal';
import { Button } from 'Components/Base';
import { t } from 'i18n';
import LibraryVideoListItem from './LibraryVideoListItem';

const i18nOpts = { scope: 'care_plan_creators.videos' };

const LibraryListModal = ({
  isOpen,
  onClose,
  libraryVideos = [],
  onSubmit,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());

  useEffect(() => {
    if (!isOpen) {
      setSearchInput('');
      setSelectedVideoIds(new Set());
    }
  }, [isOpen]);

  const filteredLibraryVideos = useMemo(() => {
    if (!searchInput.trim()) return libraryVideos;
    const lowerSearch = searchInput.toLowerCase();
    return libraryVideos.filter((video) => {
      const nameMatch = video.name.toLowerCase().includes(lowerSearch);
      const tagMatch = video.tags?.some((tag) => {
        const tagLabel = tag.label || tag;
        return tagLabel.toLowerCase().includes(lowerSearch);
      });
      return nameMatch || tagMatch;
    });
  }, [libraryVideos, searchInput]);

  const handleSearchChange = useCallback((evt) => {
    setSearchInput(evt.target.value);
  }, []);

  const handleToggleVideo = useCallback((videoId) => {
    setSelectedVideoIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  }, []);

  const handleDone = useCallback(() => {
    if (selectedVideoIds.size > 0) {
      onSubmit(Array.from(selectedVideoIds));
    }
    onClose();
  }, [selectedVideoIds, onSubmit, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={t('library_modal_title', i18nOpts)}
    >
      <div className="library-list-modal">
        <div className="library-list-modal__header">
          <div className="form-group">
            <input
              type="text"
              placeholder={t('library_search_placeholder', i18nOpts)}
              className="form-control"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
          <p className="library-list-modal__count">
            {t('videos_selected', { ...i18nOpts, count: selectedVideoIds.size })}
          </p>
        </div>

        <div className="library-list-modal__content">
          {filteredLibraryVideos.length === 0 ? (
            <p>{searchInput ? t('no_videos_found', i18nOpts) : t('all_library_videos_added', i18nOpts)}</p>
          ) : (
            <ul className="library-video-list">
              {filteredLibraryVideos.map((video) => (
                <LibraryVideoListItem
                  key={video.id}
                  video={video}
                  isSelected={selectedVideoIds.has(video.id)}
                  onToggle={handleToggleVideo}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="library-list-modal__footer">
          <Button
            text={t('done', i18nOpts)}
            className="btn btn--primary"
            onClick={handleDone}
          />
        </div>
      </div>
    </Modal>
  );
};

LibraryListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  libraryVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      tags: PropTypes.array,
    })
  ),
  onSubmit: PropTypes.func.isRequired,
};

export default LibraryListModal;
